import React, { useState } from 'react';
import OptimizedLogo from '../OptimizedImage/OptimizedLogo';
// import { TranslatedContent } from '../../../translations';
import { usePublicTranslation } from '../../../hooks/usePublicTranslation';
import { Mail, MapPin, Phone, MessageCircle, Instagram, Linkedin, ChevronDown } from 'lucide-react';
// import './Footer.css'; // Removed

// Componente de seção recolhível para mobile
interface CollapsibleSectionProps {
    title: string;
    children: React.ReactNode;
    isOpen: boolean;
    onToggle: () => void;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, children, isOpen, onToggle }) => {
    return (
        <div className="border-b border-gray-700 md:border-none">
            {/* Header - clicável no mobile */}
            <button
                className="w-full flex items-center justify-between py-3 md:py-0 md:cursor-default"
                onClick={onToggle}
            >
                <h4 className="text-secondary font-serif font-bold text-lg md:mb-4 md:border-b md:border-gray-700 md:pb-2">
                    {title}
                </h4>
                <ChevronDown
                    size={20}
                    className={`text-secondary md:hidden transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>
            {/* Conteúdo - sempre visível no desktop, toggle no mobile */}
            <div className={`overflow-hidden transition-all duration-300 md:block ${isOpen ? 'max-h-96 pb-4' : 'max-h-0'} md:max-h-none md:pb-0`}>
                {children}
            </div>
        </div>
    );
};

const Footer: React.FC = () => {
    const { t } = usePublicTranslation();

    // Estado para controlar quais seções estão abertas no mobile
    const [openSections, setOpenSections] = useState<Record<string, boolean>>({
        solutions: false,
        resources: false,
        company: false,
    });

    const toggleSection = (section: string) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    return (
        <footer id="contact" className="bg-dark text-white pt-12 pb-6">
            <div className="container mx-auto px-4">
                {/* Mobile Layout: Logo + Contact + Social first, then collapsible sections */}
                {/* Desktop Layout: Grid with all sections */}

                {/* Mobile: Logo centered */}
                <div className="flex justify-center mb-8 md:hidden">
                    <OptimizedLogo
                        name="logo-nexucapital-principal"
                        alt="NexuCapital"
                        className="h-[7rem] w-auto"
                        loading="lazy"
                    />
                </div>

                {/* Mobile: Contact & Social - always visible */}
                <div className="md:hidden mb-8">
                    <h4 className="text-secondary font-serif font-bold text-lg mb-4 border-b border-gray-700 pb-2 inline-block">
                        {t.footer.contact.title}
                    </h4>
                    <div className="space-y-2 text-sm text-gray-400">
                        <div className="flex items-center"><Mail size={20} className="mr-2 text-secondary flex-shrink-0" /> {t.footer.contact.email}</div>
                        <div className="flex items-center"><MessageCircle size={20} className="mr-2 text-secondary flex-shrink-0" /> (31) 98312-4209</div>
                        <div className="flex items-center"><Phone size={20} className="mr-2 text-secondary flex-shrink-0" /> (31) 98413-2603</div>
                        <div className="flex items-start"><MapPin size={20} className="mr-2 text-secondary mt-1 flex-shrink-0" /> <span>{t.footer.contact.address}</span></div>
                    </div>

                    {/* Social Links */}
                    <div className="flex space-x-4 mt-6">
                        <a href="https://linkedin.com/company/nexucapital" className="text-gray-400 hover:text-white transition-colors" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                            <Linkedin size={24} />
                        </a>
                        <a href="https://x.com/nexucapital" className="text-gray-400 hover:text-white transition-colors" aria-label="X" target="_blank" rel="noopener noreferrer">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                        </a>
                        <a href="https://instagram.com/nexucapital" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                            <Instagram size={24} />
                        </a>
                    </div>
                </div>

                {/* Mobile: Collapsible sections */}
                <div className="md:hidden mb-8">
                    <CollapsibleSection
                        title={t.footer.solutions.title}
                        isOpen={openSections.solutions}
                        onToggle={() => toggleSection('solutions')}
                    >
                        <ul className="space-y-2">
                            <li><a href="#solutions" className="text-gray-400 hover:text-white transition-colors">{t.footer.solutions.operations}</a></li>
                            <li><a href="#solutions" className="text-gray-400 hover:text-white transition-colors">{t.footer.solutions.insights}</a></li>
                            <li><a href="#solutions" className="text-gray-400 hover:text-white transition-colors">{t.footer.solutions.advisory}</a></li>
                        </ul>
                    </CollapsibleSection>

                    <CollapsibleSection
                        title={t.footer.resources.title}
                        isOpen={openSections.resources}
                        onToggle={() => toggleSection('resources')}
                    >
                        <ul className="space-y-2">
                            <li><span className="text-gray-400">{t.footer.resources.documentation}</span></li>
                            <li><span className="text-gray-400">{t.footer.resources.compliance}</span></li>
                            <li><span className="text-gray-400">{t.footer.resources.insights}</span></li>
                            <li><span className="text-gray-400">{t.footer.resources.support}</span></li>
                        </ul>
                    </CollapsibleSection>

                    <CollapsibleSection
                        title={t.footer.company_info.title}
                        isOpen={openSections.company}
                        onToggle={() => toggleSection('company')}
                    >
                        <ul className="space-y-2">
                            <li><a href="/about/overview" className="text-gray-400 hover:text-white transition-colors">{t.footer.company_info.about}</a></li>
                            <li><a href="/careers" className="text-gray-400 hover:text-white transition-colors">{t.footer.company_info.careers}</a></li>
                            <li><span className="text-gray-400">{t.footer.company_info.partnerships}</span></li>
                            <li><a href="/press" className="text-gray-400 hover:text-white transition-colors">{t.footer.company_info.news}</a></li>
                        </ul>
                    </CollapsibleSection>
                </div>

                {/* Desktop: Original grid layout */}
                <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-5 gap-8 mb-12">
                    {/* 1. Logo Section */}
                    <div className="flex flex-col items-start">
                        <div className="mb-4">
                            <OptimizedLogo
                                name="logo-nexucapital-principal"
                                alt="NexuCapital"
                                className="h-[8.5rem] w-auto"
                                loading="lazy"
                            />
                        </div>
                    </div>

                    {/* 2. Solutions Section */}
                    <div>
                        <h4 className="text-secondary font-serif font-bold text-lg mb-4 border-b border-gray-700 pb-2 inline-block">
                            {t.footer.solutions.title}
                        </h4>
                        <ul className="space-y-2">
                            <li><a href="#solutions" className="text-gray-400 hover:text-white transition-colors">{t.footer.solutions.operations}</a></li>
                            <li><a href="#solutions" className="text-gray-400 hover:text-white transition-colors">{t.footer.solutions.insights}</a></li>
                            <li><a href="#solutions" className="text-gray-400 hover:text-white transition-colors">{t.footer.solutions.advisory}</a></li>
                        </ul>
                    </div>

                    {/* 3. Resources Section */}
                    <div>
                        <h4 className="text-secondary font-serif font-bold text-lg mb-4 border-b border-gray-700 pb-2 inline-block">
                            {t.footer.resources.title}
                        </h4>
                        <ul className="space-y-2">
                            <li><span className="text-gray-400">{t.footer.resources.documentation}</span></li>
                            <li><span className="text-gray-400">{t.footer.resources.compliance}</span></li>
                            <li><span className="text-gray-400">{t.footer.resources.insights}</span></li>
                            <li><span className="text-gray-400">{t.footer.resources.support}</span></li>
                        </ul>
                    </div>

                    {/* 4. Company Section */}
                    <div>
                        <h4 className="text-secondary font-serif font-bold text-lg mb-4 border-b border-gray-700 pb-2 inline-block">
                            {t.footer.company_info.title}
                        </h4>
                        <ul className="space-y-2">
                            <li><a href="/about/overview" className="text-gray-400 hover:text-white transition-colors">{t.footer.company_info.about}</a></li>
                            <li><a href="/careers" className="text-gray-400 hover:text-white transition-colors">{t.footer.company_info.careers}</a></li>
                            <li><span className="text-gray-400">{t.footer.company_info.partnerships}</span></li>
                            <li><a href="/press" className="text-gray-400 hover:text-white transition-colors">{t.footer.company_info.news}</a></li>
                        </ul>
                    </div>

                    {/* 5. Contact Section */}
                    <div>
                        <h4 className="text-secondary font-serif font-bold text-lg mb-4 border-b border-gray-700 pb-2 inline-block">
                            {t.footer.contact.title}
                        </h4>
                        <div className="space-y-2 text-sm text-gray-400">
                            <div className="flex items-center"><Mail size={20} className="mr-2 text-secondary flex-shrink-0" /> {t.footer.contact.email}</div>
                            <div className="flex items-center"><MessageCircle size={20} className="mr-2 text-secondary flex-shrink-0" /> (31) 98312-4209</div>
                            <div className="flex items-center"><Phone size={20} className="mr-2 text-secondary flex-shrink-0" /> (31) 98413-2603</div>
                            <div className="flex items-start"><MapPin size={20} className="mr-2 text-secondary mt-1 flex-shrink-0" /> <span>{t.footer.contact.address}</span></div>
                        </div>

                        {/* Social Links */}
                        <div className="flex space-x-4 mt-6">
                            <a href="https://linkedin.com/company/nexucapital" className="text-gray-400 hover:text-white transition-colors" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                                <Linkedin size={20} />
                            </a>
                            <a href="https://x.com/nexucapital" className="text-gray-400 hover:text-white transition-colors" aria-label="X" target="_blank" rel="noopener noreferrer">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                            </a>
                            <a href="https://instagram.com/nexucapital" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                                <Instagram size={20} />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Bottom Section: Legal & Copyright */}
                <div className="border-t border-gray-800 pt-8 mt-4 flex flex-col md:flex-row justify-between items-center">
                    {/* Left: Copyright */}
                    <div className="text-xs text-gray-400 mb-4 md:mb-0 text-center md:text-left">
                        <p>{t.footer.copyright.replace('{{year}}', new Date().getFullYear().toString())}</p>
                    </div>

                    {/* Center/Right: Terms & Designed By */}
                    <div className="flex flex-col items-center md:items-end w-full md:w-auto">
                        <div className="flex flex-wrap justify-center md:justify-end gap-4 md:gap-6 text-sm text-gray-500 mb-2">
                            <a href="/privacy-policy" className="hover:text-white transition-colors">{t.footer.legal.privacy}</a>
                            <a href="/terms-of-service" className="hover:text-white transition-colors">{t.footer.legal.terms}</a>
                            <a href="/cookie-policy" className="hover:text-white transition-colors">{t.footer.legal.cookies}</a>
                            <a href="/legal-notice" className="hover:text-white transition-colors">{t.footer.legal.disclaimer}</a>
                        </div>
                        <div className="text-xs text-gray-600 text-center md:text-right">
                            <p>Designed by: <a href="https://ocultai.com" target="_blank" rel="noopener noreferrer" className="hover:text-gray-400">ocultai.com</a></p>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;